import { useState } from "react";
import { KeyRound } from "lucide-react";

import { AppHeader } from "./app-header";
import { EventInspector } from "./event-inspector";
import { EventsTimeline } from "./events-timeline";
import { SessionSummary } from "./session-summary";
import { TaskSidebar } from "./task-sidebar";
import { useAgentUpdates } from "./use-agent-updates";
import { Button, Input, cn } from "@workanywhere/ui";

export function AgentUpdatesPage() {
  const {
    agentApiBaseUrl,
    closeInspector,
    connectionState,
    emptyState,
    errorMessage,
    lastReceivedAt,
    mode,
    selectedRecord,
    selectedTask,
    selectedTaskId,
    setMode,
    selectTask,
    selectRecord,
    summary,
    taskErrorMessage,
    taskLoading,
    tasks,
    timelineEndRef,
    turnInProgress,
    visibleTimelineEntries,
    workspaceKey,
    hasWorkspaceKey,
    setWorkspaceKey,
    clearWorkspaceKey,
    appendPromptCommand,
  } = useAgentUpdates();
  const showInspector = mode === "developer" || selectedRecord !== null;

  if (!hasWorkspaceKey) {
    return <WorkspaceKeyGate onSubmit={setWorkspaceKey} />;
  }

  return (
    <main className="flex h-screen min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <AppHeader connectionState={connectionState} lastReceivedAt={lastReceivedAt} mode={mode} onModeChange={setMode} />

      <section
        className={cn(
          "grid min-h-0 flex-1 grid-cols-1 overflow-hidden",
          showInspector ? "lg:grid-cols-[280px_minmax(0,1fr)_360px]" : "lg:grid-cols-[280px_minmax(0,1fr)]",
        )}
      >
        <TaskSidebar
          errorMessage={taskErrorMessage}
          loading={taskLoading}
          selectedTaskId={selectedTaskId}
          tasks={tasks}
          onOpenAnotherWorkspace={clearWorkspaceKey}
          onSelectTask={selectTask}
        />

        <div className="flex min-h-0 flex-col overflow-hidden border-b lg:border-b-0 lg:border-r">
          <SessionSummary
            summary={summary}
            connectionState={connectionState}
            errorMessage={errorMessage}
            selectedTask={selectedTask}
          />
          <EventsTimeline
            agentApiBaseUrl={agentApiBaseUrl}
            currentTaskId={selectedTaskId}
            emptyState={emptyState}
            mode={mode}
            entries={visibleTimelineEntries}
            selectedRecord={selectedRecord}
            selectedTaskTitle={selectedTask?.title ?? null}
            timelineEndRef={timelineEndRef}
            turnInProgress={turnInProgress}
            workspaceKey={workspaceKey}
            onPromptSubmitted={appendPromptCommand}
            onSelectRecord={selectRecord}
          />
        </div>

        {showInspector ? <EventInspector record={selectedRecord} onClose={mode === "normal" ? closeInspector : undefined} /> : null}
      </section>
    </main>
  );
}

function WorkspaceKeyGate({ onSubmit }: { onSubmit: (workspaceKey: string) => void }) {
  const [value, setValue] = useState("");

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <form
        className="grid w-full max-w-md gap-4 rounded-lg border bg-card p-5 shadow-sm"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value);
        }}
      >
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <KeyRound className="size-4" />
          </div>
          <div>
            <h1 className="text-base font-semibold leading-none">Workspace Key</h1>
            <p className="mt-1 text-xs text-muted-foreground">Paste the key from the desktop app.</p>
          </div>
        </div>
        <Input
          aria-label="Workspace key"
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="wk_..."
        />
        <Button type="submit">Open workspace</Button>
      </form>
    </main>
  );
}
